import { useEffect, useRef, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { FaFileDownload } from "react-icons/fa";
import dayjs from "dayjs";
import style from "../style.module.scss";
import { API_BASE_URL } from "../../../utilities/API_CONSTANT";
import { API_URLs } from "../../../utilities/API_URLs";
import { Http } from "../../../utilities/axios/http";
import { downloadPDF } from "../../../utilities/downloadPdf";
import { ExitNoteStateConstant } from "../core/_constant.releaseOrder";
import { StatusTag } from "../../../common/components/Status/StatusTag";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

type ExitNotePdfViewerProps = {
  exitNoteId: number | string | null;
  reference?: string;
  exitDate?: string | null;
  state?: { id?: string; name?: string } | null;
};

/** Releases the object URL currently held, if any. */
const revokeUrl = (urlRef: { current: string | null }) => {
  if (urlRef.current) {
    URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  }
};

/**
 * Fetches GET /exit_notes/{id}/pdf as a blob and shows it in the drawer, with a
 * download shortcut. The backend serves the PDF frozen at generation time.
 */
export const ExitNotePdfViewer = ({
  exitNoteId,
  reference,
  exitDate,
  state,
}: ExitNotePdfViewerProps) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const objectUrlRef = useRef<string | null>(null);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      revokeUrl(objectUrlRef);
    };
  }, []);

  useEffect(() => {
    if (!exitNoteId) {
      return;
    }

    let isStale = false;

    const loadPdf = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const url = `${API_BASE_URL}${API_URLs.exitNote.exitNotePdfUrl.replace(
          "{id}",
          String(exitNoteId)
        )}`;
        const res = await Http.getFile(url);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const objectUrl = URL.createObjectURL(blob);

        if (isStale || !isMountedRef.current) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        revokeUrl(objectUrlRef);
        objectUrlRef.current = objectUrl;
        setPdfUrl(objectUrl);
      } catch (error) {
        console.log(error);
        if (!isStale) {
          setHasError(true);
        }
      } finally {
        if (!isStale) {
          setIsLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      isStale = true;
    };
  }, [exitNoteId]);

  const handleDownload = async () => {
    if (!exitNoteId) return;

    try {
      setIsDownloading(true);
      await downloadPDF(
        API_URLs.exitNote.exitNotePdfUrl.replace("{id}", String(exitNoteId))
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={style.pdfViewer}>
      <div className={style.pdfViewer__toolbar}>
        <div className={style.pdfViewer__info}>
          <span className={style.pdfViewer__reference}>
            {reference ? `Bon de sortie ${reference}` : "Bon de sortie"}
          </span>

          <span className={style.pdfViewer__meta}>
            {exitDate
              ? `Date de sortie : ${dayjs(exitDate, "DD/MM/YYYY HH:mm:ss").format("DD/MM/YYYY")}`
              : "Aperçu du document"}
            {state?.id && (
              <>
                {" · "}
                <StatusTag
                  value={state.id}
                  options={ExitNoteStateConstant.OPTIONS}
                />
              </>
            )}
          </span>
        </div>

        {isDownloading ? (
          <Spin indicator={<LoadingOutlined spin />} size="small" />
        ) : (
          <div
            className={style.pdfViewer__download}
            onClick={handleDownload}
            title="Télécharger le PDF"
          >
            <FaFileDownload />
            <span>Télécharger</span>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className={style.pdfViewer__placeholder}>
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      ) : hasError || !pdfUrl ? (
        <div className={style.pdfViewer__placeholder}>
          <span className={style.pdfViewer__meta}>
            Impossible de charger le bon de sortie.
          </span>
        </div>
      ) : (
        <iframe
          key={pdfUrl}
          title={`Bon de sortie ${reference ?? ""}`}
          src={pdfUrl}
          className={style.pdfViewer__frame}
        />
      )}
    </div>
  );
};
